import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import AtriumApi, { getApiErrorMessage } from "api";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";
import { QRCode } from "react-qr-code";
import { MdDownload, MdQrCode, MdContentCopy, MdPrint, MdSettings } from "react-icons/md";
import { PageContainer, PageHeader } from "components/page";
import Card, { CardHeader } from "components/card";
import Button, { IconButton } from "components/button";
import { Select } from "components/fields";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";

const SIZE_OPTIONS = [
  { value: 128, label: "Small (128x128)" },
  { value: 256, label: "Medium (256x256)" },
  { value: 512, label: "Large (512x512)" },
  { value: 1024, label: "Extra Large (1024x1024)" },
];

const STEPS = [
  {
    title: "Download or Print",
    body: "Download the QR code as an image or print it directly for physical distribution.",
  },
  {
    title: "Add to Materials",
    body: "Include the QR code in your bulletins, flyers, presentations, or signage.",
  },
  {
    title: "Visitors Scan & Connect",
    body: "Visitors can scan the QR code with their phone camera to instantly access your Link Hub.",
  },
];

const TIPS = [
  "Use larger sizes for printed materials (512px or above)",
  "Ensure good contrast between QR code and background when printing",
  "Test the QR code after printing to ensure it scans properly",
  "Place QR codes where they're easily accessible to visitors",
];

const UrlRow = ({ label, hint, url, copyLabel, onCopy }) => (
  <div>
    <p className="mb-1.5 text-sm font-medium text-fg">
      {label}
      {hint && <span className="ml-1 font-normal text-fg-muted">{hint}</span>}
    </p>
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded-control bg-surface-raised px-3 py-2 font-mono text-sm text-fg">
        {url}
      </code>
      <IconButton
        icon={<MdContentCopy />}
        label={copyLabel}
        variant="secondary"
        onClick={onCopy}
      />
    </div>
  </div>
);

const QRCodeGenerator = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [qrSize, setQrSize] = useState(256);
  const qrRef = useRef(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await AtriumApi.get("/connect/profile");
      if (response.data.success) {
        setProfile(response.data.data);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        setLoadError(error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    track(EVENTS.QR_CODE_VIEWED);
    fetchProfile();
  }, [fetchProfile]);

  const publicUrl = profile?.slug
    ? `${window.location.origin}/connect/${profile.slug}`
    : "";

  const shortUrl = profile?.shortUrl || null;

  // Use short URL for QR if available, otherwise use public URL
  const qrUrl = shortUrl || publicUrl;

  const copyUrl = async (url, type) => {
    try {
      await navigator.clipboard.writeText(url);
      notify.success(`${type} copied`);
    } catch (error) {
      notify.error(getApiErrorMessage(error, `Could not copy the ${type.toLowerCase()}`));
    }
  };

  const downloadQRCode = (format = "png") => {
    const svg = qrRef.current.querySelector("svg");
    if (!svg) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();

    canvas.width = qrSize;
    canvas.height = qrSize;

    img.onload = () => {
      // The exported image needs an opaque white background behind the code.
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, qrSize, qrSize);
      ctx.drawImage(img, 0, 0, qrSize, qrSize);

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${profile.slug}-qr-code.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        notify.success("QR code downloaded");
      }, `image/${format}`);
    };

    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  const printQRCode = () => {
    const printWindow = window.open("", "_blank");
    const svg = qrRef.current.querySelector("svg");
    if (!svg) return;

    const displayUrl = shortUrl || publicUrl;

    printWindow.document.write(`
      <html>
        <head>
          <title>QR Code - ${profile.displayName}</title>
          <style>
            body {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              font-family: Arial, sans-serif;
            }
            .container {
              text-align: center;
              padding: 40px;
            }
            h1 {
              margin-bottom: 10px;
              font-size: 24px;
            }
            .url {
              margin-bottom: 30px;
              font-size: 18px;
              font-weight: bold;
              color: #333;
            }
            .qr-code {
              margin: 30px auto;
            }
            @media print {
              body {
                background: white;
              }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${profile.displayName}</h1>
            <div class="url">${displayUrl}</div>
            <div class="qr-code">${svg.outerHTML}</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  const header = (
    <PageHeader title="QR Code" description="Download and share your Link Hub QR code" />
  );

  if (loading) {
    return (
      <PageContainer>
        {header}
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Skeleton className="h-[36rem] w-full rounded-card" />
          <Skeleton className="h-96 w-full rounded-card" />
        </div>
      </PageContainer>
    );
  }

  if (loadError) {
    return (
      <PageContainer>
        {header}
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(loadError, "Could not load your Link Hub")}
            onRetry={fetchProfile}
          />
        </Card>
      </PageContainer>
    );
  }

  if (!profile) {
    return (
      <PageContainer>
        {header}
        <Card padding="md">
          <EmptyState
            icon={MdQrCode}
            title="No Link Hub Yet"
            description="Create your Link Hub profile first, then come back to download its QR code."
            action={
              <Button as={Link} to="/admin/connect/customize" leftIcon={<MdSettings />}>
                Create Link Hub
              </Button>
            }
          />
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {header}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card padding="md" className="gap-5">
          <CardHeader title="Your QR Code" />

          <div className="flex flex-col gap-4">
            {shortUrl && (
              <UrlRow
                label="Short URL"
                hint="(Recommended)"
                url={shortUrl}
                copyLabel="Copy short URL"
                onCopy={() => copyUrl(shortUrl, "Short URL")}
              />
            )}
            <UrlRow
              label="Full URL"
              url={publicUrl}
              copyLabel="Copy URL"
              onCopy={() => copyUrl(publicUrl, "URL")}
            />
          </div>

          <div>
            {/* The QR code always renders on white so it scans in dark mode too. */}
            <div
              ref={qrRef}
              className="overflow-x-auto rounded-control border border-line bg-white p-8"
            >
              <div className="mx-auto w-max">
                <QRCode value={qrUrl} size={qrSize} level="H" />
              </div>
            </div>
            {shortUrl && (
              <p className="mt-2 text-center text-xs text-fg-muted">
                QR code points to your short URL for easier sharing
              </p>
            )}
          </div>

          <Select
            label="QR Code Size"
            value={qrSize}
            onChange={(e) => setQrSize(Number(e.target.value))}
            options={SIZE_OPTIONS}
          />

          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              leftIcon={<MdDownload />}
              onClick={() => downloadQRCode("png")}
              className="sm:flex-1"
            >
              Download PNG
            </Button>
            <Button
              variant="secondary"
              leftIcon={<MdPrint />}
              onClick={printQRCode}
              className="sm:flex-1"
            >
              Print QR Code
            </Button>
          </div>
        </Card>

        <Card padding="md" className="gap-5 self-start">
          <CardHeader title="How to Use Your QR Code" />

          <ol className="flex flex-col gap-3">
            {STEPS.map((step, index) => (
              <li
                key={step.title}
                className="flex gap-3 rounded-control border border-line p-4"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700 dark:bg-brand-900/40 dark:text-brand-200">
                  {index + 1}
                </span>
                <div>
                  <h3 className="font-semibold text-fg">{step.title}</h3>
                  <p className="mt-1 text-sm text-fg-muted">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="border-t border-line pt-5">
            <h3 className="font-semibold text-fg">Tips for Best Results</h3>
            <ul className="mt-2 list-disc space-y-2 pl-5 text-sm text-fg-muted marker:text-brand-500">
              {TIPS.map((tip) => (
                <li key={tip}>{tip}</li>
              ))}
            </ul>
          </div>
        </Card>
      </div>
    </PageContainer>
  );
};

export default QRCodeGenerator;
